import type { DetailSession } from './detail-session'
import type { SessionResultsData } from '~/gql/sessions/sessions'
import { CalendarClock, Lock } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { DATE_WITH_YEAR, formatSessionDate } from '~/helpers/dates/session-dates'
import { tourAnchor } from '~/helpers/tour/anchors'
import { chapterSegments, chaptersFrom } from '~/pages/backoffice/admin/sessions/path-chapters'

type IProps = {
  session: Pick<DetailSession, 'modules' | 'sections'>
  /** Complétions par module ; absentes pour qui ne voit pas les statistiques. */
  results?: Pick<SessionResultsData, 'modules' | 'participants'>
  timeZone?: string | null
}

/** Le parcours de la session, chapitre par chapitre, avec ses verrous et ses complétions (#243). */
function SessionPathCard({ session, results, timeZone }: IProps) {
  const { t, i18n } = useTranslation()
  const titleOf = new Map(session.modules.map(step => [step.module.id, step.module.title]))
  const completedOf = new Map(results?.modules.map(m => [m.moduleId, m.completedCount]))
  const segments = chapterSegments(session.modules, chaptersFrom(session.modules, session.sections))

  return (
    <Card {...tourAnchor('sessionDetail.path')}>
      <CardHeader>
        <CardTitle className="text-lg">{t('sessions.path.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {session.modules.length === 0 && <p className="text-sm text-muted-foreground">{t('sessionDetail.path.empty')}</p>}
        {segments.map(segment => (
          <section key={segment.start} className="flex flex-col gap-2">
            {segment.chapter && <h3 className="text-sm font-semibold">{segment.chapter.title}</h3>}
            <ol className="flex flex-col divide-y rounded-lg border" start={segment.start + 1}>
              {segment.steps.map((step, i) => {
                const prerequisite = step.prerequisiteModuleId ? titleOf.get(step.prerequisiteModuleId) : undefined
                return (
                  <li key={step.id} className="flex flex-wrap items-start justify-between gap-3 p-3">
                    <div className="flex min-w-0 flex-col gap-1">
                      <span className="font-medium">
                        {/* Le rang est déjà dit par la liste ordonnée : visible, il n'est pas relu. */}
                        <span aria-hidden="true">{`${segment.start + i + 1}. `}</span>
                        {step.module.title}
                      </span>
                      {prerequisite && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Lock className="size-3 shrink-0" aria-hidden="true" />
                          {step.prerequisiteMinScore != null
                            ? t('sessionDetail.path.prerequisiteScore', { title: prerequisite, score: Math.round(step.prerequisiteMinScore * 100) })
                            : t('sessionDetail.path.prerequisite', { title: prerequisite })}
                        </span>
                      )}
                      {step.unlockAt && (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <CalendarClock className="size-3 shrink-0" aria-hidden="true" />
                          {`${t('sessions.path.opensOn')} ${formatSessionDate(step.unlockAt, i18n.language, timeZone, DATE_WITH_YEAR)}`}
                        </span>
                      )}
                    </div>
                    {results && (
                      <span className="text-sm tabular-nums text-muted-foreground">
                        {t('sessionDetail.path.completed', { completed: completedOf.get(step.module.id) ?? 0, total: results.participants.length })}
                      </span>
                    )}
                  </li>
                )
              })}
            </ol>
          </section>
        ))}
      </CardContent>
    </Card>
  )
}

export default SessionPathCard
